import { useState, useEffect } from 'react';
import { FaTrash, FaSpinner, FaPlus, FaVolumeUp, FaEye, FaLanguage } from 'react-icons/fa';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { generateBatchSpeechAudio, LanguageCodes, VoiceGenders } from '../../../utilities/extensions/textToSpeech';
import { translateBatchToText } from '../../../utilities/extensions/translateToText';
import Constants from '../../../utilities/constants/Constants';
import { db } from '../../../firebase';
import { generateActivityStoragePath } from '../../../utilities/storagePathHelper';

const MakeSentencesActivity = ({ initialContent, onContentChange, chapterId, grade, selectedChapter }) => {
  const [content, setContent] = useState({
    sentences: [{
      languageWord: '',
      englishWord: '',
      languageSentence: '',
      englishSentence: '',
      languageWordAudio: null,
      englishWordAudio: null,
      languageSentenceAudio: null,
      englishSentenceAudio: null,
      image: null,
      keywords: []
    }]
  });
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [translatingKeywords, setTranslatingKeywords] = useState(false);

  // Build storage path using the helper
  const buildStoragePath = ({
    sentenceId,
    keywordId,
    fileName
  }) => {
    const language = selectedChapter?.subject?.toLowerCase() || 'language';
    const gradeStr = `grade${grade}`;

    return generateActivityStoragePath({
      language,
      grade: gradeStr,
      chapterId,
      activityName: 'make_sentences',
      fileName,
      itemId: keywordId ? `${sentenceId}_${keywordId}` : sentenceId
    });
  };

  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = JSON.parse(initialContent);

        // Ensure backward-compatible shape: sentences array and activity-level keywords
        let sentences = Array.isArray(parsed.sentences) && parsed.sentences.length > 0
          ? parsed.sentences
          : content.sentences;

        // Backward compatibility: if there are activity-level keywords, move them to the first sentence
        if (Array.isArray(parsed.keywords) && parsed.keywords.length > 0 && sentences.length > 0) {
          if (!sentences[0].keywords || sentences[0].keywords.length === 0) {
            sentences[0].keywords = parsed.keywords.map(k => {
              if (k && typeof k === 'object' && 'keyword' in k && !('languageText' in k)) {
                return {
                  languageText: k.keyword || '',
                  languageAudio: null,
                  englishText: '',
                  englishAudio: null,
                  confusingLetters: k.confusingLetters || ''
                };
              }
              return {
                languageText: k.languageText || '',
                languageAudio: k.languageAudio || null,
                englishText: k.englishText || '',
                englishAudio: k.englishAudio || null,
                confusingLetters: k.confusingLetters || ''
              };
            });
          }
        }

        // Ensure every sentence has a keywords array
        sentences = sentences.map(s => ({
          ...s,
          keywords: Array.isArray(s.keywords) ? s.keywords : []
        }));

        setContent({
          sentences
        });
      } catch {
        // keep default
      }
    }
  }, [initialContent]);

  useEffect(() => {
    onContentChange(content);
  }, [content, onContentChange]);

  const addSentence = () => {
    const newSentences = [
      ...content.sentences,
      {
        languageWord: '',
        englishWord: '',
        languageSentence: '',
        englishSentence: '',
        languageWordAudio: null,
        englishWordAudio: null,
        languageSentenceAudio: null,
        englishSentenceAudio: null,
        image: null,
        keywords: []
      }
    ];
    setContent(prev => ({ ...prev, sentences: newSentences }));
  };

  // Sentence-level keywords helpers
  const addKeyword = (sentenceIndex) => {
    setContent(prev => {
      const updated = [...prev.sentences];
      updated[sentenceIndex] = {
        ...updated[sentenceIndex],
        keywords: [
          ...(updated[sentenceIndex].keywords || []),
          {
            languageText: '',
            languageAudio: null,
            englishText: '',
            englishAudio: null,
            confusingLetters: ''
          }
        ]
      };
      return { ...prev, sentences: updated };
    });
  };

  const removeKeyword = (sentenceIndex, keywordIndex) => {
    setContent(prev => {
      const updated = [...prev.sentences];
      const keywords = [...(updated[sentenceIndex].keywords || [])];
      keywords.splice(keywordIndex, 1);
      updated[sentenceIndex] = {
        ...updated[sentenceIndex],
        keywords
      };
      return { ...prev, sentences: updated };
    });
  };

  const updateKeywordField = (sentenceIndex, keywordIndex, field, value) => {
    setContent(prev => {
      const updated = [...prev.sentences];
      const keywords = [...(updated[sentenceIndex].keywords || [])];
      keywords[keywordIndex] = {
        ...keywords[keywordIndex],
        [field]: value
      };
      updated[sentenceIndex] = {
        ...updated[sentenceIndex],
        keywords
      };
      return { ...prev, sentences: updated };
    });
  };

  const handleKeywordFileUpload = async (sentenceIndex, keywordIndex, field, file) => {
    if (!file || !chapterId || !grade) return;

    const uploadKey = `s${sentenceIndex}-k${keywordIndex}-${field}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const storage = getStorage();
      const sentenceId = `sentence${sentenceIndex + 1}`;
      const keywordId = `keyword${keywordIndex + 1}`;

      const filePath = buildStoragePath({
        sentenceId,
        keywordId,
        fileName: file.name
      });

      const fileRef = ref(storage, filePath);
      const snapshot = await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      updateKeywordField(sentenceIndex, keywordIndex, field, downloadURL);
    } catch (error) {
      alert('Upload failed. Please try again.');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const removeSentence = async (index) => {
    if (!content.sentences || content.sentences.length <= 1) return;

    const sentence = content.sentences[index];
    const storage = getStorage();

    const fileTypes = [
      'languageWordAudio',
      'englishWordAudio',
      'languageSentenceAudio',
      'englishSentenceAudio',
      'image'
    ];

    // Remove sentence-level files
    for (const fileType of fileTypes) {
      const filePath = sentence[fileType];
      if (filePath) {
        try {
          const fileRef = ref(storage, filePath);
          await deleteObject(fileRef);
        } catch (error) {
          // ignore errors
        }
      }
    }

    // Remove keyword-level files
    if (Array.isArray(sentence.keywords)) {
      for (const k of sentence.keywords) {
        if (k.languageAudio) {
          try {
            await deleteObject(ref(storage, k.languageAudio));
          } catch (e) {
            // Ignore deletion errors for missing files
          }
        }
        if (k.englishAudio) {
          try {
            await deleteObject(ref(storage, k.englishAudio));
          } catch (e) {
            // Ignore deletion errors for missing files
          }
        }
      }
    }

    const updated = content.sentences.filter((_, i) => i !== index);
    setContent(prev => ({ ...prev, sentences: updated }));
  };

  const updateSentence = (index, field, value) => {
    const updated = [...content.sentences];
    updated[index] = { ...updated[index], [field]: value };
    setContent(prev => ({ ...prev, sentences: updated }));
  };

  const uploadFile = async (file, index, fileType) => {
    if (!file || !chapterId || !grade) {
      alert('Missing required information for file upload');
      return;
    }

    const uploadKey = `${index}-${fileType}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const storage = getStorage();
      const sentenceId = `sentence${index + 1}`;

      const storagePath = buildStoragePath({
        sentenceId,
        fileName: file.name
      });

      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);

      updateSentence(index, fileType, storagePath);
    } catch (error) {
      alert(`Failed to upload file: ${error.message}`);
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  // Translate all keywords for all sentences
  const translateAllKeywords = async () => {
    const textsToTranslate = [];
    const mapping = []; // { sIndex, kIndex }

    content.sentences.forEach((sentence, sIndex) => {
      (sentence.keywords || []).forEach((k, kIndex) => {
        if (k.languageText && k.languageText.trim() && !k.englishText) {
          textsToTranslate.push(k.languageText);
          mapping.push({ sIndex, kIndex });
        }
      });
    });

    if (textsToTranslate.length === 0) {
      alert('All keywords already have English text or no language text to translate.');
      return;
    }

    setTranslatingKeywords(true);

    try {
      const translatedTexts = await translateBatchToText(
        textsToTranslate,
        db,
        5,
        2000,
        Constants.LANGUAGES.KANNADA,
        Constants.LANGUAGES.ENGLISH
      );

      const updatedSentences = [...content.sentences];
      translatedTexts.forEach((text, idx) => {
        if (!text || text.startsWith('[Translation Error:')) return;
        const { sIndex, kIndex } = mapping[idx];
        if (!updatedSentences[sIndex].keywords[kIndex]) return;
        
        updatedSentences[sIndex].keywords[kIndex] = {
          ...updatedSentences[sIndex].keywords[kIndex],
          englishText: text
        };
      });

      setContent(prev => ({ ...prev, sentences: updatedSentences }));
      alert(`Successfully translated ${translatedTexts.filter(t => t && !t.startsWith('[Translation Error:')).length} keywords!`);
    } catch (error) {
      alert('Failed to translate keywords: ' + error.message);
    } finally {
      setTranslatingKeywords(false);
    }
  };

  // Generate audio for all keywords in all sentences
  const generateAudioForKeywords = async () => {
    const ttsRequests = [];

    content.sentences.forEach((sentence, sIndex) => {
      (sentence.keywords || []).forEach((k, kIndex) => {
        if (k.languageText && k.languageText.trim() && !k.languageAudio) {
          ttsRequests.push({
            text: k.languageText,
            languageCode: LanguageCodes.KANNADA,
            ssmlGender: VoiceGenders.FEMALE,
            sIndex,
            kIndex,
            type: 'keywordLanguage'
          });
        }
        if (k.englishText && k.englishText.trim() && !k.englishAudio) {
          ttsRequests.push({
            text: k.englishText,
            languageCode: LanguageCodes.ENGLISH,
            ssmlGender: VoiceGenders.FEMALE,
            sIndex,
            kIndex,
            type: 'keywordEnglish'
          });
        }
      });
    });

    if (ttsRequests.length === 0) {
      alert('All keywords already have audio for the available texts');
      return;
    }

    setGeneratingAudio(true);

    try {
      const storage = getStorage();
      const audioUrls = await generateBatchSpeechAudio(
        ttsRequests.map(req => ({
          text: req.text,
          languageCode: req.languageCode,
          ssmlGender: req.ssmlGender
        })),
        db,
        storage
      );

      const updatedSentences = [...content.sentences];
      ttsRequests.forEach((request, requestIndex) => {
        const audioUrl = audioUrls[requestIndex];
        if (!audioUrl || audioUrl.startsWith('[TTS Error:')) return;

        const { sIndex, kIndex, type } = request;
        if (!updatedSentences[sIndex].keywords[kIndex]) return;

        if (type === 'keywordLanguage') {
          updatedSentences[sIndex].keywords[kIndex].languageAudio = audioUrl;
        } else if (type === 'keywordEnglish') {
          updatedSentences[sIndex].keywords[kIndex].englishAudio = audioUrl;
        }
      });

      setContent(prev => ({ ...prev, sentences: updatedSentences }));
      alert('Generated audio for keywords!');
    } catch (error) {
      alert('Failed to generate keyword audio: ' + error.message);
    } finally {
      setGeneratingAudio(false);
    }
  };

  const removeFile = async (index, fileType) => {
    const sentence = content.sentences[index];
    if (!sentence[fileType]) return;

    try {
      const storage = getStorage();
      const fileRef = ref(storage, sentence[fileType]);
      await deleteObject(fileRef);
      updateSentence(index, fileType, null);
    } catch (error) {
      alert(`Failed to delete file: ${error.message}`);
    }
  };

  const playAudio = async (audioPath) => {
    if (!audioPath) return;

    try {
      const storage = getStorage();
      const audioRef = ref(storage, audioPath);
      const downloadURL = await getDownloadURL(audioRef);
      const audio = new Audio(downloadURL);
      audio.play().catch(() => {});
    } catch (error) {
      // ignore errors
    }
  };

  // Generate audio for existing sentences that are missing audio
  const generateAudioForExistingSentences = async () => {
    if (!content.sentences || content.sentences.length === 0) {
      alert('No sentences available for audio generation');
      return;
    }

    const ttsRequests = [];

    content.sentences.forEach((sentence, index) => {
      if (
        sentence.languageWord &&
        sentence.languageWord.trim() &&
        !sentence.languageWordAudio
      ) {
        ttsRequests.push({
          text: sentence.languageWord,
          languageCode: LanguageCodes.KANNADA,
          ssmlGender: VoiceGenders.FEMALE,
          sentenceIndex: index,
          type: 'languageWord'
        });
      }

      if (
        sentence.englishWord &&
        sentence.englishWord.trim() &&
        !sentence.englishWordAudio
      ) {
        ttsRequests.push({
          text: sentence.englishWord,
          languageCode: LanguageCodes.ENGLISH,
          ssmlGender: VoiceGenders.FEMALE,
          sentenceIndex: index,
          type: 'englishWord'
        });
      }

      if (
        sentence.languageSentence &&
        sentence.languageSentence.trim() &&
        !sentence.languageSentenceAudio
      ) {
        ttsRequests.push({
          text: sentence.languageSentence,
          languageCode: LanguageCodes.KANNADA,
          ssmlGender: VoiceGenders.FEMALE,
          sentenceIndex: index,
          type: 'languageSentence'
        });
      }

      if (
        sentence.englishSentence &&
        sentence.englishSentence.trim() &&
        !sentence.englishSentenceAudio
      ) {
        ttsRequests.push({
          text: sentence.englishSentence,
          languageCode: LanguageCodes.ENGLISH,
          ssmlGender: VoiceGenders.FEMALE,
          sentenceIndex: index,
          type: 'englishSentence'
        });
      }
    });

    if (ttsRequests.length === 0) {
      alert('All sentences already have audio for the available texts');
      return;
    }

    setGeneratingAudio(true);

    try {
      const storage = getStorage();

      const audioUrls = await generateBatchSpeechAudio(
        ttsRequests.map(req => ({
          text: req.text,
          languageCode: req.languageCode,
          ssmlGender: req.ssmlGender
        })),
        db,
        storage
      );

      const updatedSentences = [...content.sentences];

      ttsRequests.forEach((request, requestIndex) => {
        const audioUrl = audioUrls[requestIndex];
        if (!audioUrl || audioUrl.startsWith('[TTS Error:')) return;

        const { sentenceIndex, type } = request;
        if (type === 'languageWord') {
          updatedSentences[sentenceIndex].languageWordAudio = audioUrl;
        } else if (type === 'englishWord') {
          updatedSentences[sentenceIndex].englishWordAudio = audioUrl;
        } else if (type === 'languageSentence') {
          updatedSentences[sentenceIndex].languageSentenceAudio = audioUrl;
        } else if (type === 'englishSentence') {
          updatedSentences[sentenceIndex].englishSentenceAudio = audioUrl;
        }
      });

      setContent(prev => ({ ...prev, sentences: updatedSentences }));
    } catch (error) {
      alert('Failed to generate audio: ' + error.message);
    } finally {
      setGeneratingAudio(false);
    }
  };

  const validate = () => {
    const errors = [];

    if (!content.sentences || content.sentences.length === 0) {
      errors.push('At least one sentence is required');
    }

    content.sentences?.forEach((sentence, index) => {
      if (!sentence.languageWord?.trim()) {
        errors.push(`Language word is required for sentence ${index + 1}`);
      }
      if (!sentence.englishWord?.trim()) {
        errors.push(`English word is required for sentence ${index + 1}`);
      }
      if (!sentence.languageSentence?.trim()) {
        errors.push(`Language sentence is required for sentence ${index + 1}`);
      }
      if (!sentence.englishSentence?.trim()) {
        errors.push(`English sentence is required for sentence ${index + 1}`);
      }
    });

    return errors;
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              ✏️ <span className="ml-2">Make Sentences</span>
            </h3>
            <p className="text-sm text-gray-600 mt-1">{content.sentences.length} sentence{content.sentences.length !== 1 ? 's' : ''} configured</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium shadow-sm text-sm disabled:bg-purple-300 disabled:cursor-not-allowed"
              onClick={translateAllKeywords}
              disabled={translatingKeywords || generatingAudio}
            >
              {translatingKeywords ? (
                <>
                  <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                  Translating...
                </>
              ) : (
                <>
                  <FaLanguage className="w-4 h-4 mr-2" />
                  Translate All Keywords
                </>
              )}
            </button>
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium shadow-sm text-sm disabled:bg-orange-300 disabled:cursor-not-allowed"
              onClick={generateAudioForKeywords}
              disabled={generatingAudio || translatingKeywords}
            >
              {generatingAudio ? (
                <>
                  <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                  Generating Audio...
                </>
              ) : (
                <>
                  <FaVolumeUp className="w-4 h-4 mr-2" />
                  Generate All Keyword Audio
                </>
              )}
            </button>
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
              onClick={addSentence}
            >
              <FaPlus className="w-4 h-4 mr-2" />
              Add Sentence
            </button>
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium shadow-sm disabled:bg-orange-300 disabled:cursor-not-allowed text-sm"
              onClick={generateAudioForExistingSentences}
              disabled={generatingAudio || !content.sentences || content.sentences.length === 0}
            >
              {generatingAudio ? (
                <>
                  <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FaVolumeUp className="w-4 h-4 mr-2" />
                  Generate Sentence Audio
                </>
              )}
            </button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {content.sentences.map((sentence, sIndex) => (
            <div key={sIndex} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                  {sIndex + 1}
                </div>
                <div className="flex-1 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                      placeholder="Language word *"
                      value={sentence.languageWord || ''}
                      onChange={(e) => updateSentence(sIndex, 'languageWord', e.target.value)}
                    />
                    <input
                      type="text"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                      placeholder="English word *"
                      value={sentence.englishWord || ''}
                      onChange={(e) => updateSentence(sIndex, 'englishWord', e.target.value)}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                      placeholder="Language sentence *"
                      value={sentence.languageSentence || ''}
                      onChange={(e) => updateSentence(sIndex, 'languageSentence', e.target.value)}
                    />
                    <input
                      type="text"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                      placeholder="English sentence *"
                      value={sentence.englishSentence || ''}
                      onChange={(e) => updateSentence(sIndex, 'englishSentence', e.target.value)}
                    />
                  </div>

                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    {/* Sentence Audio Controls */}
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        <input
                          type="file"
                          accept="audio/*"
                          className="hidden"
                          id={`lw-audio-${sIndex}`}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) uploadFile(file, sIndex, 'languageWordAudio');
                          }}
                          disabled={uploadingFiles[`${sIndex}-languageWordAudio`]}
                        />
                        <label
                          htmlFor={`lw-audio-${sIndex}`}
                          className="cursor-pointer px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100 transition-colors"
                          title="Language Word Audio"
                        >
                          🎵 LW
                        </label>
                        {uploadingFiles[`${sIndex}-languageWordAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-blue-600" />}
                        {sentence.languageWordAudio && !uploadingFiles[`${sIndex}-languageWordAudio`] && (
                          <div className="flex items-center gap-0.5">
                            <button type="button" className="p-1 text-green-600 hover:bg-green-100 rounded" onClick={() => playAudio(sentence.languageWordAudio)}><FaVolumeUp className="w-3 h-3" /></button>
                            <button type="button" className="p-1 text-red-600 hover:bg-red-100 rounded" onClick={() => removeFile(sIndex, 'languageWordAudio')}><FaTrash className="w-3 h-3" /></button>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <input
                          type="file"
                          accept="audio/*"
                          className="hidden"
                          id={`ew-audio-${sIndex}`}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) uploadFile(file, sIndex, 'englishWordAudio');
                          }}
                          disabled={uploadingFiles[`${sIndex}-englishWordAudio`]}
                        />
                        <label
                          htmlFor={`ew-audio-${sIndex}`}
                          className="cursor-pointer px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100 transition-colors"
                          title="English Word Audio"
                        >
                          🎵 EW
                        </label>
                        {uploadingFiles[`${sIndex}-englishWordAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-blue-600" />}
                        {sentence.englishWordAudio && !uploadingFiles[`${sIndex}-englishWordAudio`] && (
                          <div className="flex items-center gap-0.5">
                            <button type="button" className="p-1 text-green-600 hover:bg-green-100 rounded" onClick={() => playAudio(sentence.englishWordAudio)}><FaVolumeUp className="w-3 h-3" /></button>
                            <button type="button" className="p-1 text-red-600 hover:bg-red-100 rounded" onClick={() => removeFile(sIndex, 'englishWordAudio')}><FaTrash className="w-3 h-3" /></button>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <input
                          type="file"
                          accept="audio/*"
                          className="hidden"
                          id={`ls-audio-${sIndex}`}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) uploadFile(file, sIndex, 'languageSentenceAudio');
                          }}
                          disabled={uploadingFiles[`${sIndex}-languageSentenceAudio`]}
                        />
                        <label
                          htmlFor={`ls-audio-${sIndex}`}
                          className="cursor-pointer px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100 transition-colors"
                          title="Language Sentence Audio"
                        >
                          🎵 LS
                        </label>
                        {uploadingFiles[`${sIndex}-languageSentenceAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-blue-600" />}
                        {sentence.languageSentenceAudio && !uploadingFiles[`${sIndex}-languageSentenceAudio`] && (
                          <div className="flex items-center gap-0.5">
                            <button type="button" className="p-1 text-green-600 hover:bg-green-100 rounded" onClick={() => playAudio(sentence.languageSentenceAudio)}><FaVolumeUp className="w-3 h-3" /></button>
                            <button type="button" className="p-1 text-red-600 hover:bg-red-100 rounded" onClick={() => removeFile(sIndex, 'languageSentenceAudio')}><FaTrash className="w-3 h-3" /></button>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <input
                          type="file"
                          accept="audio/*"
                          className="hidden"
                          id={`es-audio-${sIndex}`}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) uploadFile(file, sIndex, 'englishSentenceAudio');
                          }}
                          disabled={uploadingFiles[`${sIndex}-englishSentenceAudio`]}
                        />
                        <label
                          htmlFor={`es-audio-${sIndex}`}
                          className="cursor-pointer px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100 transition-colors"
                          title="English Sentence Audio"
                        >
                          🎵 ES
                        </label>
                        {uploadingFiles[`${sIndex}-englishSentenceAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-blue-600" />}
                        {sentence.englishSentenceAudio && !uploadingFiles[`${sIndex}-englishSentenceAudio`] && (
                          <div className="flex items-center gap-0.5">
                            <button type="button" className="p-1 text-green-600 hover:bg-green-100 rounded" onClick={() => playAudio(sentence.englishSentenceAudio)}><FaVolumeUp className="w-3 h-3" /></button>
                            <button type="button" className="p-1 text-red-600 hover:bg-red-100 rounded" onClick={() => removeFile(sIndex, 'englishSentenceAudio')}><FaTrash className="w-3 h-3" /></button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Image */}
                    <div className="flex items-center gap-1">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        id={`img-${sIndex}`}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) uploadFile(file, sIndex, 'image');
                        }}
                        disabled={uploadingFiles[`${sIndex}-image`]}
                      />
                      <label
                        htmlFor={`img-${sIndex}`}
                        className="cursor-pointer px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs hover:bg-purple-100 transition-colors"
                        title="Upload Image"
                      >
                        🖼️ IMG
                      </label>
                      {uploadingFiles[`${sIndex}-image`] && <FaSpinner className="w-3 h-3 animate-spin text-purple-600" />}
                      {sentence.image && !uploadingFiles[`${sIndex}-image`] && (
                        <div className="flex items-center gap-0.5">
                          <button
                            type="button"
                            className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                            onClick={async () => {
                              try {
                                const storage = getStorage();
                                const imageRef = ref(storage, sentence.image);
                                const downloadURL = await getDownloadURL(imageRef);
                                window.open(downloadURL, '_blank');
                              } catch (error) {
                                alert(`Failed to preview image. Error: ${error.message}`);
                              }
                            }}
                          >
                            <FaEye className="w-3 h-3" />
                          </button>
                          <button type="button" className="p-1 text-red-600 hover:bg-red-100 rounded" onClick={() => removeFile(sIndex, 'image')}><FaTrash className="w-3 h-3" /></button>
                        </div>
                      )}
                    </div>

                    {/* Remove sentence */}
                    {content.sentences.length > 1 && (
                      <button
                        type="button"
                        className="ml-auto p-2 text-red-600 hover:bg-red-100 rounded transition-colors"
                        onClick={() => removeSentence(sIndex)}
                        title="Remove Sentence"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Keywords Section for this sentence */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-700 flex items-center">
                        🔑 <span className="ml-1.5">Sentence Keywords</span>
                      </h4>
                      <button
                        type="button"
                        className="flex items-center px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs hover:bg-blue-100 transition-colors font-medium"
                        onClick={() => addKeyword(sIndex)}
                      >
                        <FaPlus className="w-3 h-3 mr-1" />
                        Add Keyword
                      </button>
                    </div>
                    <div className="space-y-3">
                      {(sentence.keywords || []).map((keyword, kIndex) => (
                        <div key={kIndex} className="bg-white p-3 rounded border border-gray-200 relative group">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
                            <input
                              type="text"
                              className="px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500"
                              placeholder="Keyword (language) *"
                              value={keyword.languageText || ''}
                              onChange={(e) => updateKeywordField(sIndex, kIndex, 'languageText', e.target.value)}
                            />
                            <input
                              type="text"
                              className="px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500"
                              placeholder="Keyword (English)"
                              value={keyword.englishText || ''}
                              onChange={(e) => updateKeywordField(sIndex, kIndex, 'englishText', e.target.value)}
                            />
                          </div>
                          <div className="flex items-center gap-3">
                            <input
                              type="text"
                              className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500"
                              placeholder="Confusing letters (optional)"
                              value={keyword.confusingLetters || ''}
                              onChange={(e) => updateKeywordField(sIndex, kIndex, 'confusingLetters', e.target.value)}
                            />
                            <div className="flex items-center gap-2">
                              {/* Keyword Audio Controls */}
                              <div className="flex items-center gap-1">
                                <input
                                  type="file"
                                  accept="audio/*"
                                  className="hidden"
                                  id={`kw-la-${sIndex}-${kIndex}`}
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) handleKeywordFileUpload(sIndex, kIndex, 'languageAudio', file);
                                  }}
                                  disabled={uploadingFiles[`s${sIndex}-k${kIndex}-languageAudio`]}
                                />
                                <label htmlFor={`kw-la-${sIndex}-${kIndex}`} className="cursor-pointer p-1.5 bg-gray-100 rounded hover:bg-gray-200" title="Upload Language Audio">
                                  <FaVolumeUp className="w-3 h-3 text-gray-600" />
                                </label>
                                {uploadingFiles[`s${sIndex}-k${kIndex}-languageAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-blue-600" />}
                                {keyword.languageAudio && !uploadingFiles[`s${sIndex}-k${kIndex}-languageAudio`] && (
                                  <button type="button" className="p-1 text-green-600 hover:bg-green-100 rounded" onClick={() => playAudio(keyword.languageAudio)}><FaVolumeUp className="w-3 h-3" /></button>
                                )}
                              </div>
                              <button
                                type="button"
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                onClick={() => removeKeyword(sIndex, kIndex)}
                                title="Remove Keyword"
                              >
                                <FaTrash className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Validation Display */}
      {(() => {
        const errors = validate();
        if (errors.length > 0) {
          return (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <div className="flex items-start">
                <div className="w-6 h-6 text-amber-600 mr-3 mt-0.5">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-amber-800 mb-2">Please fix the following issues:</h3>
                  <ul className="space-y-1">
                    {errors.map((error, index) => (
                      <li key={index} className="flex items-start text-amber-700">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                        <span>{error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}
    </div>
  );
};

export const validateMakeSentencesContent = (content) => {
  const errors = [];

  if (!content.sentences || content.sentences.length === 0) {
    errors.push('At least one sentence is required');
  }

  content.sentences?.forEach((sentence, index) => {
    if (!sentence.languageWord?.trim()) {
      errors.push(`Language word is required for sentence ${index + 1}`);
    }
    if (!sentence.englishWord?.trim()) {
      errors.push(`English word is required for sentence ${index + 1}`);
    }
    if (!sentence.languageSentence?.trim()) {
      errors.push(`Language sentence is required for sentence ${index + 1}`);
    }
    if (!sentence.englishSentence?.trim()) {
      errors.push(`English sentence is required for sentence ${index + 1}`);
    }
  });

  return errors;
};

export default MakeSentencesActivity;
